import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, CheckCircle2, Phone, User, Mail, Shield, FileText, Hash, Radio } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../../lib/api";
import { formatAbsolute } from "../../lib/datetime";
import {
  AGENT_TOOL_NAMES,
  STATE_TRANSITIONS,
  TRANSITION_VARIANT,
  deleteEngagement,
  testCall,
  type AgentToolName,
  type EngagementState,
  type TransitionVariant,
  type VoicePipelineMode,
  type SipTrunkChoice,
} from "../../api/engagements";
import { fetchTargets } from "../../api/targets";
import {
  useEngagement,
  useTransitionEngagement,
  useUpdateEngagement,
  usePhoneAllowlistImport,
  useEmailAllowlistImport,
} from "../../hooks/useEngagement";
import { usePersona } from "../../hooks/usePersona";
import { useEngagementCalls } from "../../hooks/useCalls";
import { Card } from "../../components/Card";
import { StateBadge } from "../../components/StateBadge";
import { ConfirmModal } from "../../components/ConfirmModal";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { AllowlistField } from "../../components/AllowlistField";
import { EmailResultsPanel } from "../../components/EmailResultsPanel";
import { VideoDrillPanel } from "../../components/video/VideoDrillPanel";
import { PageHeader } from "../../components/ui/PageHeader";
import {
  FormField,
  inputStyle,
  readonlyMonoStyle,
  readonlyValueStyle,
} from "../../components/ui/FormField";
import { StateMachineHeader } from "./StateMachineHeader";
import { PersonaPicker } from "./PersonaPicker";
import { DispatchCallModal } from "./DispatchCallModal";
import { buildLiveKitMeetUrl } from "../../api/dispatch";
import type { StateBadgeKind } from "../../components/StateBadge";

// ─── Helpers ────────────────────────────────────────────────────────────────────

function stateToBadgeKind(state: EngagementState): StateBadgeKind {
  switch (state) {
    case "active":               return "active";
    case "draft":                return "draft";
    case "paused":               return "paused";
    case "pending_authorization":return "info";
    case "concluded":            return "concluded";
    case "kill_switch_locked":   return "error";
    case "revoked":              return "error";
  }
}

function stateLabel(state: EngagementState): string {
  const labels: Record<EngagementState, string> = {
    draft: "DRAFT",
    pending_authorization: "PENDING AUTH",
    active: "ACTIVE",
    paused: "PAUSED",
    concluded: "CONCLUDED",
    kill_switch_locked: "KILL SWITCH",
    revoked: "REVOKED",
  };
  return labels[state];
}

function transitionLabel(target: EngagementState, source?: EngagementState): string {
  const labels: Record<EngagementState, string> = {
    draft: "← BACK TO DRAFT",
    pending_authorization: "SUBMIT FOR AUTH",
    active: source === "paused" ? "RESUME" : "ACTIVATE",
    paused: "PAUSE",
    concluded: "CONCLUDE",
    kill_switch_locked: "KILL SWITCH",
    revoked: "REVOKE",
  };
  return labels[target];
}

interface VariantStyle {
  bg: string;
  border: string;
  color: string;
}

function variantStyle(variant: TransitionVariant): VariantStyle {
  switch (variant) {
    case "safe":
      return { bg: "var(--accent)", border: "var(--accent)", color: "#04181A" };
    case "neutral":
      return {
        bg: "var(--bg-tertiary)",
        border: "var(--border)",
        color: "var(--text-secondary)",
      };
    case "warning":
      return {
        bg: "transparent",
        border: "var(--warning)",
        color: "var(--warning)",
      };
    case "danger":
      return {
        bg: "transparent",
        border: "var(--error)",
        color: "var(--error)",
      };
    case "critical":
      return {
        bg: "var(--error)",
        border: "var(--error)",
        color: "#FFFFFF",
      };
  }
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return formatAbsolute(iso);
}

function callStateColor(state: string): string {
  switch (state) {
    case "LIVE":   return "var(--accent)";
    case "ENDED":  return "var(--text-primary)";
    case "HALTED": return "var(--error)";
    default:       return "var(--text-primary)";
  }
}

function callVerdictColor(verdict: string): string {
  switch (verdict) {
    case "CLEAN":  return "var(--success)";
    case "REJECT": return "var(--warning)";
    case "FAILED": return "var(--error)";
    default:       return "var(--text-secondary)";
  }
}

function tierLabel(tier: string): string {
  return tier.replace("_", " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// ─── Field row for the snapshot rail ────────────────────────────────────────────

function StatRow({
  label,
  value,
  mono,
  accent,
}: {
  label: string;
  value: string;
  mono?: boolean;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        padding: "7px 0",
        gap: 12,
        borderBottom: "1px dashed var(--border-subtle)",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: 9.5,
          letterSpacing: "0.16em",
          color: "var(--text-muted)",
          textTransform: "uppercase",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
          color: accent ? "var(--accent)" : "var(--text-primary)",
          fontSize: 12,
          fontWeight: 500,
          textAlign: "right",
          wordBreak: "break-all",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </span>
    </div>
  );
}

// ─── Detail page ────────────────────────────────────────────────────────────────

export function EngagementDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Queries + mutations
  const { data, isLoading, isError } = useEngagement(id ?? "");
  const updateMutation = useUpdateEngagement(id ?? "");
  const transitionMutation = useTransitionEngagement(id ?? "");
  const phoneImport = usePhoneAllowlistImport(id ?? "");
  const emailImport = useEmailAllowlistImport(id ?? "");
  const { data: personaData } = usePersona(data?.record.persona_id ?? "");

  // UI state
  const [editName, setEditName] = useState<string | null>(null);
  const [transitionTarget, setTransitionTarget] = useState<EngagementState | null>(null);
  const [transitionComment, setTransitionComment] = useState("");
  const [transitionReason, setTransitionReason] = useState("");
  const [transitionError, setTransitionError] = useState<string | null>(null);
  const [showPersonaPicker, setShowPersonaPicker] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [showTestCallModal, setShowTestCallModal] = useState(false);

  // Delete state
  const [confirmDelete, setConfirmDelete] = useState(false);
  const deleteMut = useMutation({
    mutationFn: () => deleteEngagement(id!),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["engagements"] });
      navigate("/engagements");
    },
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : "Failed to delete engagement."),
  });

  if (!id) {
    return <StatusBlock label="Missing engagement ID." tone="error" />;
  }
  if (isLoading) return <StatusBlock label="Loading engagement…" />;
  if (isError || !data) {
    return (
      <StatusBlock label="Failed to load engagement." tone="error">
        <button
          onClick={() => navigate("/engagements")}
          style={{
            marginTop: 14,
            background: "transparent",
            border: "1px solid var(--accent)",
            color: "var(--accent)",
            padding: "6px 14px",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.14em",
            cursor: "pointer",
            borderRadius: "var(--radius-input)",
          }}
        >
          ← BACK TO LIST
        </button>
      </StatusBlock>
    );
  }

  const rec = data.record;
  const validTransitions = STATE_TRANSITIONS[rec.state];
  const deletableStates: EngagementState[] = ["draft", "concluded", "revoked"];
  const canDelete = deletableStates.includes(rec.state);
  const phoneEntries = rec.phone_allowlist?.allowed_phones_e164 ?? [];
  const emailEntries = rec.email_allowlist?.allowed_emails ?? [];

  function handleNameBlur() {
    const trimmed = (editName ?? "").trim();
    if (trimmed && trimmed !== rec.display_name) {
      updateMutation.mutate({ display_name: trimmed });
    }
    setEditName(null);
  }

  function openTransition(target: EngagementState) {
    setTransitionTarget(target);
    setTransitionComment("");
    setTransitionReason("");
    setTransitionError(null);
  }

  function closeTransition() {
    setTransitionTarget(null);
    setTransitionComment("");
    setTransitionReason("");
    setTransitionError(null);
    transitionMutation.reset();
  }

  async function confirmTransition() {
    if (!transitionTarget) return;
    if (transitionTarget === "kill_switch_locked" && !transitionReason.trim()) {
      setTransitionError("A reason is required to engage the kill switch.");
      return;
    }
    setTransitionError(null);
    try {
      await transitionMutation.mutateAsync({
        target_state: transitionTarget,
        comment: transitionComment || undefined,
        reason:
          transitionTarget === "kill_switch_locked"
            ? transitionReason.trim()
            : undefined,
      });
      closeTransition();
    } catch (err) {
      if (err instanceof ApiError) {
        setTransitionError(`Server error ${err.status}: ${err.detail}`);
      } else {
        setTransitionError("Transition failed — unknown error");
      }
    }
  }

  function handlePersonaSelect(personaId: string | null) {
    updateMutation.mutate({ persona_id: personaId });
  }

  async function handlePhonePreview(file: File) {
    const result = await phoneImport.preview.mutateAsync(file);
    return {
      preview_token: result.preview_token,
      validCount: result.valid.length,
      duplicateCount: result.duplicates.length,
      invalidCount: result.invalid.length,
      totalRows: result.total_rows,
    };
  }
  async function handlePhoneCommit(preview_token: string) {
    await phoneImport.commit.mutateAsync(preview_token);
  }
  async function handleEmailPreview(file: File) {
    const result = await emailImport.preview.mutateAsync(file);
    return {
      preview_token: result.preview_token,
      validCount: result.valid.length,
      duplicateCount: result.duplicates.length,
      invalidCount: result.invalid.length,
      totalRows: result.total_rows,
    };
  }
  async function handleEmailCommit(preview_token: string) {
    await emailImport.commit.mutateAsync(preview_token);
  }

  const transitionButtons = (
    <>
      {rec.is_template && rec.persona_id && (
        <button
          onClick={() => setShowTestCallModal(true)}
          title="Send a one-shot test call to a verified test-pool number"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 14px",
            borderRadius: "var(--radius-input)",
            border: "1px solid var(--accent)",
            background: "var(--accent)",
            color: "#04181A",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.14em",
            fontWeight: 600,
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          <Radio size={12} strokeWidth={2} />
          SEND TEST CALL
        </button>
      )}
      {rec.state === "active" && rec.persona_id && !rec.is_template && (
        <button
          onClick={() => setShowDispatchModal(true)}
          title="Dispatch a LiveKit call against an allowlisted phone"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "7px 14px",
            borderRadius: "var(--radius-input)",
            border: "1px solid var(--cta)",
            background: "var(--cta)",
            color: "#1A1108",
            fontFamily: "var(--font-mono)",
            fontSize: 11,
            letterSpacing: "0.14em",
            fontWeight: 600,
            textTransform: "uppercase",
            cursor: "pointer",
            boxShadow: "var(--glow-cta)",
          }}
        >
          <Radio size={12} strokeWidth={2} />
          DISPATCH CALL
        </button>
      )}
      {validTransitions.map((target) => {
        const s = variantStyle(TRANSITION_VARIANT[target]);
        return (
          <button
            key={target}
            onClick={() => openTransition(target)}
            title={`Transition: ${stateLabel(rec.state)} → ${stateLabel(target)}`}
            style={{
              padding: "7px 14px",
              borderRadius: "var(--radius-input)",
              fontFamily: "var(--font-mono)",
              fontSize: 10.5,
              fontWeight: 600,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              border: `1px solid ${s.border}`,
              background: s.bg,
              color: s.color,
              cursor: "pointer",
            }}
          >
            {transitionLabel(target, rec.state)}
          </button>
        );
      })}
      {canDelete && (
        <button
          type="button"
          onClick={() => setConfirmDelete(true)}
          style={{
            padding: "7px 14px",
            borderRadius: "var(--radius-input)",
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            fontWeight: 600,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            border: "1px solid var(--error)",
            background: "transparent",
            color: "var(--error)",
            cursor: "pointer",
          }}
        >
          DELETE
        </button>
      )}
    </>
  );

  return (
    <div style={{ fontFamily: "var(--font-sans)" }}>
      <PageHeader
        eyebrow={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 10 }}>
            <Link
              to="/engagements"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                color: "var(--text-muted)",
                textDecoration: "none",
                fontFamily: "var(--font-mono)",
                fontSize: 10,
                letterSpacing: "0.18em",
              }}
            >
              <ArrowLeft size={11} strokeWidth={1.5} />
              ENGAGEMENTS
            </Link>
            <span style={{ color: "var(--border-strong)" }}>/</span>
            <span style={{ color: "var(--text-secondary)" }}>{rec.engagement_id.slice(0, 8)}</span>
          </span>
        }
        title={
          <span style={{ display: "inline-flex", alignItems: "center", gap: 16 }}>
            <span>{rec.display_name}</span>
            <StateBadge kind={stateToBadgeKind(rec.state)}>
              {stateLabel(rec.state)}
            </StateBadge>
          </span>
        }
        meta={
          <span>
            TIER <span style={{ color: "var(--text-secondary)" }}>{tierLabel(rec.autonomy_tier)}</span>
            {rec.channel === "voice" && (
              <>
                <Dot />
                PIPELINE <span style={{ color: "var(--accent)" }}>{rec.voice_pipeline_mode}</span>
              </>
            )}
            <Dot />
            <span style={{ color: "var(--text-muted)" }}>{rec.engagement_id}</span>
          </span>
        }
        actions={
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, justifyContent: "flex-end" }}>
            {transitionButtons}
          </div>
        }
      />

      {/* State-machine visualization just below the header */}
      <div
        style={{
          marginBottom: 22,
          padding: "14px 18px",
          background: "var(--bg-card)",
          border: "1px solid var(--border-subtle)",
          borderRadius: "var(--radius-card)",
        }}
      >
        <StateMachineHeader current={rec.state} />
      </div>

      {/* ── Two-column layout ── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 300px",
          gap: 18,
          alignItems: "start",
        }}
      >
        {/* Left column */}
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

          <Card title="Identity">
            <FormField label="Display name">
              <input
                value={editName ?? rec.display_name}
                onChange={(e) => setEditName(e.target.value)}
                onFocus={() => setEditName(rec.display_name)}
                onBlur={handleNameBlur}
                style={inputStyle}
              />
            </FormField>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <FormField label="Customer ID">
                <div style={readonlyMonoStyle}>{rec.customer_id}</div>
              </FormField>
              <FormField label="Autonomy tier">
                <div style={readonlyValueStyle}>{tierLabel(rec.autonomy_tier)}</div>
              </FormField>
            </div>

            {rec.channel === "voice" && (
              <>
                <FormField
                  label="Voice pipeline"
                  hint="TTS-gated: Cartesia TTS with prophylactic Enforcer gating."
                >
                  <select
                    value={rec.voice_pipeline_mode}
                    onChange={(e) =>
                      updateMutation.mutate({
                        voice_pipeline_mode: e.target.value as VoicePipelineMode,
                      })
                    }
                    style={inputStyle}
                  >
                    <option value="tts_gated">TTS-gated · Cartesia</option>
                  </select>
                </FormField>

                <FormField
                  label="SIP trunk"
                  hint="Outbound carrier for PSTN dialing. Default uses the worker's configured trunk."
                >
                  <select
                    value={rec.sip_trunk_choice ?? ""}
                    onChange={(e) =>
                      updateMutation.mutate({
                        sip_trunk_choice:
                          e.target.value === ""
                            ? null
                            : (e.target.value as SipTrunkChoice),
                      })
                    }
                    style={inputStyle}
                  >
                    <option value="">Default (worker config)</option>
                    <option value="telnyx">Telnyx</option>
                    <option value="signalwire">SignalWire</option>
                  </select>
                </FormField>
              </>
            )}

            <FormField label="Engagement ID">
              <div style={readonlyMonoStyle}>{rec.engagement_id}</div>
            </FormField>
          </Card>

          {rec.channel === "voice" && (
            <Card title="Agent tools (evidence capture)" accentColor="var(--accent)">
              <AgentToolsToggle
                enabled={rec.agent_tools_enabled ?? []}
                onChange={(next) =>
                  updateMutation.mutate({ agent_tools_enabled: next })
                }
                busy={updateMutation.isPending}
              />
            </Card>
          )}

          {rec.channel === "voice" && (
            <Card title="Call duration cap" accentColor="var(--warning)">
              <MaxTurnsControl
                value={rec.max_turns_per_call ?? null}
                onChange={(next) =>
                  updateMutation.mutate({ max_turns_per_call: next })
                }
                busy={updateMutation.isPending}
              />
            </Card>
          )}

          {rec.channel === "voice" && (
            <Card title="Call recording" accentColor="var(--accent)">
              <RecordingToggles
                recordingEnabled={rec.recording_enabled}
                disclosureRequired={rec.recording_disclosure_required}
                onChange={(next) => updateMutation.mutate(next)}
                busy={updateMutation.isPending}
              />
            </Card>
          )}

          {rec.channel === "voice" && (
            <Card title="Streaming gating (latency vs. safety)" accentColor="var(--warning)">
              <StreamingGatingToggle
                enabled={rec.streaming_gating_enabled ?? false}
                onChange={(next) => updateMutation.mutate(next)}
                busy={updateMutation.isPending}
              />
            </Card>
          )}

          {rec.channel === "voice" && (
          <Card title="Persona binding">
            {rec.persona_id ? (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto 1fr auto",
                  alignItems: "center",
                  gap: 14,
                  background: "var(--bg-tertiary)",
                  border: "1px solid var(--border-subtle)",
                  borderLeft: "2px solid var(--accent)",
                  borderRadius: "var(--radius-input)",
                  padding: "12px 14px",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    border: "1px solid var(--accent-line)",
                    display: "grid",
                    placeItems: "center",
                    color: "var(--accent)",
                  }}
                >
                  <User size={14} strokeWidth={1.5} />
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      fontSize: 9.5,
                      letterSpacing: "0.18em",
                      color: "var(--text-muted)",
                    }}
                  >
                    PERSONA BOUND
                  </span>
                  {personaData ? (
                    <>
                      <span
                        style={{
                          fontSize: 12.5,
                          fontWeight: 600,
                          color: "var(--text-primary)",
                        }}
                      >
                        {personaData.record.display_name}
                      </span>
                      {personaData.record.role && (
                        <span
                          style={{
                            fontSize: 11,
                            color: "var(--text-muted)",
                          }}
                        >
                          {personaData.record.role}
                        </span>
                      )}
                      <span
                        style={{
                          fontFamily: "var(--font-mono)",
                          fontSize: 9.5,
                          color: "var(--text-muted)",
                          letterSpacing: "0.04em",
                          wordBreak: "break-all",
                          marginTop: 1,
                        }}
                      >
                        {rec.persona_id}
                      </span>
                    </>
                  ) : (
                    <span
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color: "var(--text-secondary)",
                        letterSpacing: "0.04em",
                        wordBreak: "break-all",
                      }}
                    >
                      {rec.persona_id}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setShowPersonaPicker(true)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "var(--radius-input)",
                    border: "1px solid var(--border)",
                    background: "var(--bg-secondary)",
                    color: "var(--text-secondary)",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.14em",
                    fontWeight: 500,
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  Change
                </button>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  background: "transparent",
                  border: "1px dashed var(--border)",
                  borderRadius: "var(--radius-input)",
                  padding: "14px 16px",
                }}
              >
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 11,
                    letterSpacing: "0.12em",
                    color: "var(--text-muted)",
                    textTransform: "uppercase",
                  }}
                >
                  No persona bound
                </span>
                <button
                  onClick={() => setShowPersonaPicker(true)}
                  style={{
                    padding: "6px 14px",
                    borderRadius: "var(--radius-input)",
                    border: "1px solid var(--accent)",
                    background: "var(--accent)",
                    color: "#04181A",
                    fontFamily: "var(--font-mono)",
                    fontSize: 10,
                    letterSpacing: "0.14em",
                    fontWeight: 600,
                    cursor: "pointer",
                    textTransform: "uppercase",
                  }}
                >
                  Bind Persona
                </button>
              </div>
            )}
            <p
              style={{
                margin: 0,
                fontSize: 10.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
                letterSpacing: "-0.005em",
              }}
            >
              Persona must be in{" "}
              <code
                style={{
                  fontFamily: "var(--font-mono)",
                  color: "var(--accent)",
                  fontSize: 10.5,
                }}
              >
                ACTIVE
              </code>{" "}
              state to bind. Draft and retired personas cannot be attached.
            </p>
          </Card>
          )}

          {!rec.is_template && rec.channel === "voice" && (
            <Card title="Phone allowlist">
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--text-muted)",
                  marginBottom: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Phone size={11} strokeWidth={1.5} />
                Calls only land if{" "}
                <code style={{ fontFamily: "var(--font-mono)", color: "var(--accent)" }}>
                  target.phone ∈ allowlist
                </code>
              </div>
              <AllowlistField
                label="Phone allowlist"
                entries={phoneEntries}
                onPreview={handlePhonePreview}
                onCommit={handlePhoneCommit}
                exportFilename={`phones-${rec.engagement_id}.csv`}
                hint={`CSV format · one E.164 number per row, header optional:\n  phone,label\n  +1 714 642 4588,Jane Doe (VP Eng)\n  +1 415 555 0123,Mark Stevens\n✓ E.164 syntax · duplicates dropped · max 500 rows\n! labels are advisory; only phone is enforced`}
              />
            </Card>
          )}

          {!rec.is_template && rec.channel === "email" && (
            <Card title="Email allowlist">
              <div
                style={{
                  fontSize: 10.5,
                  color: "var(--text-muted)",
                  marginBottom: 4,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Mail size={11} strokeWidth={1.5} />
                For future email channel
              </div>
              <AllowlistField
                label="Email allowlist"
                entries={emailEntries}
                onPreview={handleEmailPreview}
                onCommit={handleEmailCommit}
                exportFilename={`emails-${rec.engagement_id}.csv`}
                hint={`CSV format · one address per row, header optional:\n  email,label\n  jane@acme.example,Jane Doe\n  mark@acme.example,Mark Stevens\n✓ RFC 5322 syntax · duplicates dropped · max 1000 rows`}
              />
            </Card>
          )}

          {!rec.is_template && (
            <Card title="Rules of engagement (ROE)" accentColor="var(--text-muted)">
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 12,
                  padding: "8px 0",
                }}
              >
                <FileText size={14} strokeWidth={1.5} color="var(--text-muted)" />
                <p
                  style={{
                    margin: 0,
                    fontSize: 11.5,
                    color: "var(--text-muted)",
                    lineHeight: 1.6,
                  }}
                >
                  ROE documents are managed out-of-band — the console stores a
                  structured{" "}
                  <code style={{ fontFamily: "var(--font-mono)", fontSize: 10.5, color: "var(--text-secondary)" }}>
                    ROEDocumentRef
                  </code>{" "}
                  (URI + version + signed_by + signed_at), not a plain text field. Sign the document, then use the CLI to bind it to this engagement.
                </p>
              </div>
            </Card>
          )}

          {/* Video Drill — the engagement-level config + test workspace
              (script/avatar/consent/render/dispatch). Lives in the MAIN column
              as the primary surface for a video engagement; shows on templates
              too (unlike the email RESULTS panel, which is post-campaign). */}
          {rec.channel === "video" && <VideoDrillPanel rec={rec} />}
        </div>

        {/* ── Right rail ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Card title="Snapshot" accentColor="var(--success)">
            <StatRow label="State" value={stateLabel(rec.state)} accent />
            <StatRow label="Created" value={formatDate(rec.created_at)} mono />
            {rec.activated_at && (
              <StatRow label="Activated" value={formatDate(rec.activated_at)} mono />
            )}
            {rec.concluded_at && (
              <StatRow label="Concluded" value={formatDate(rec.concluded_at)} mono />
            )}
            {rec.revoked_at && (
              <StatRow label="Revoked" value={formatDate(rec.revoked_at)} mono />
            )}
            <StatRow label="Phone entries" value={String(phoneEntries.length)} mono />
            <StatRow label="Email entries" value={String(emailEntries.length)} mono />
          </Card>

          {rec.channel === "voice" && (
            <RecentCallsForEngagement engagementId={rec.engagement_id} />
          )}

          <Card title="Audit chain" accentColor="var(--cta)">
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                fontSize: 10.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
              }}
            >
              <Hash size={12} strokeWidth={1.5} />
              <span>
                Audit chain query is post-bundle. Hash + event count will appear here once the
                audit log endpoint is wired into the detail view.
              </span>
            </div>
          </Card>

          <Card title="Safety" accentColor="var(--error)">
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 10,
                fontSize: 10.5,
                color: "var(--text-muted)",
                lineHeight: 1.6,
              }}
            >
              <Shield size={12} strokeWidth={1.5} />
              <span>
                Kill-switch transition is destructive and audit-logged with operator identity +
                reason. Use only when off-script behavior is observed.
              </span>
            </div>
          </Card>
        </div>
      </div>

      {rec.channel === "email" && !rec.is_template && (
        <div style={{ marginTop: 18 }}>
          <EmailResultsPanel
            engagementId={rec.engagement_id}
            live={rec.state === "active"}
          />
        </div>
      )}

      {/* ── Transition confirm modal ── */}
      <ConfirmModal
        open={transitionTarget !== null}
        title={`Transition to ${transitionTarget ? stateLabel(transitionTarget) : ""}`}
        description={
          transitionTarget == null
            ? undefined
            : transitionTarget === "kill_switch_locked"
              ? `KILL SWITCH locks ${stateLabel(rec.state)} → KILL SWITCH. The engagement cannot be reactivated — restart requires a fresh signed ROE. Provide a reason; it is recorded with your operator identity in the audit chain.`
              : transitionTarget === "revoked"
                ? `REVOKE moves ${stateLabel(rec.state)} → REVOKED. Terminal — the engagement cannot run again.`
                : transitionTarget === "concluded"
                  ? `CONCLUDE moves ${stateLabel(rec.state)} → CONCLUDED. Terminal — normal close-out.`
                  : `Move the engagement from ${stateLabel(rec.state)} to ${stateLabel(transitionTarget)}.`
        }
        confirmLabel={transitionTarget ? transitionLabel(transitionTarget, rec.state) : "Confirm"}
        danger={
          transitionTarget === "revoked" ||
          transitionTarget === "kill_switch_locked"
        }
        withComment
        comment={transitionComment}
        onCommentChange={setTransitionComment}
        loading={transitionMutation.isPending}
        error={transitionError}
        onConfirm={() => { void confirmTransition(); }}
        onCancel={closeTransition}
      >
        {transitionTarget === "kill_switch_locked" && (
          <FormField label="Reason" suffix="REQUIRED">
            <input
              value={transitionReason}
              onChange={(e) => setTransitionReason(e.target.value)}
              placeholder="e.g. operator observed off-script behavior on call 5184cec2"
              maxLength={500}
              style={{
                ...inputStyle,
                borderColor: "var(--error)",
              }}
            />
          </FormField>
        )}
      </ConfirmModal>

      <PersonaPicker
        open={showPersonaPicker}
        currentPersonaId={rec.persona_id}
        onSelect={handlePersonaSelect}
        onClose={() => setShowPersonaPicker(false)}
      />

      <DispatchCallModal
        open={showDispatchModal}
        engagementId={rec.engagement_id}
        engagementName={rec.display_name}
        allowedPhones={phoneEntries}
        onClose={() => setShowDispatchModal(false)}
      />

      {showTestCallModal && (
        <TestCallModal
          engagementId={rec.engagement_id}
          onClose={() => setShowTestCallModal(false)}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title="Delete engagement?"
        body="Calls, recordings, and audit records are preserved. This cannot be undone."
        busy={deleteMut.isPending}
        onConfirm={() => deleteMut.mutate()}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}

// ─── Test Call Modal ─────────────────────────────────────────────────────────────

function TestCallModal({ engagementId, onClose }: { engagementId: string; onClose: () => void }) {
  const { data } = useQuery({ queryKey: ["targets"], queryFn: () => fetchTargets() });
  const [result, setResult] = useState<{ call_id: string; livekit_url: string; operator_access_token: string } | null>(null);
  const [dispatchError, setDispatchError] = useState<string | null>(null);

  const callMut = useMutation({
    mutationFn: (targetId: string) => testCall(engagementId, targetId),
    onSuccess: (res) => {
      setResult({ call_id: res.call_id, livekit_url: res.livekit_url, operator_access_token: res.operator_access_token });
      setDispatchError(null);
    },
    onError: (err: unknown) => {
      if (err instanceof ApiError) {
        setDispatchError(`${err.status}: ${err.detail}`);
      } else {
        setDispatchError("Call failed — unknown error");
      }
    },
  });

  const allTargets = data?.items ?? [];
  // Verified test pool ONLY: test_only numbers the operator has OTP-verified.
  // These bypass ROE on the test-call path; the server rejects anything else
  // with 409 test_requires_verified_pool. Real targeting is campaigns-only
  // (signed ROE + target groups), so no any-target source here.
  const rows = allTargets.filter((t) => t.test_only && t.phone_verified && t.primary_phone_e164);

  return (
    <div style={{
      position: "fixed", inset: 0, zIndex: 200,
      background: "rgba(0,0,0,0.55)",
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <div style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-card)",
        padding: 24,
        width: 420,
        maxWidth: "90vw",
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        gap: 16,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--text-muted)" }}>
            Send Test Call
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 18, lineHeight: 1 }}>×</button>
        </div>

        {result ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ fontSize: 12, color: "var(--success)", fontFamily: "var(--font-mono)" }}>
              Call dispatched: {result.call_id}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Link
                to={`/live-calls/${result.call_id}`}
                onClick={onClose}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 14px",
                  borderRadius: "var(--radius-input)",
                  border: "1px solid var(--accent)",
                  background: "var(--accent)",
                  color: "#04181A",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  textDecoration: "none",
                }}
              >
                Go to Live Call
              </Link>
              <a
                href={buildLiveKitMeetUrl(result.livekit_url, result.operator_access_token)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "7px 14px",
                  borderRadius: "var(--radius-input)",
                  border: "1px solid var(--border)",
                  background: "transparent",
                  color: "var(--text-secondary)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 11,
                  letterSpacing: "0.14em",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  textDecoration: "none",
                }}
              >
                Listen In (LiveKit) ↗
              </a>
            </div>
            <button onClick={onClose} style={{
              padding: "6px 14px",
              borderRadius: "var(--radius-input)",
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              cursor: "pointer",
            }}>Close</button>
          </div>
        ) : (
          <>
            <p style={{ margin: 0, fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.5 }}>
              Pick one of your verified test numbers. These bypass ROE on the
              test-call path only — they can never be dialed by a real campaign.
              To add or verify a number, go to Targets → Test pool.
            </p>
            {dispatchError && (
              <div style={{ fontSize: 12, color: "var(--error)", fontFamily: "var(--font-mono)" }}>{dispatchError}</div>
            )}
            <div style={{ overflowY: "auto", maxHeight: 280, display: "flex", flexDirection: "column", gap: 2 }}>
              {rows.length === 0 ? (
                <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)", padding: "8px 0", lineHeight: 1.5 }}>
                  No verified test numbers yet. Add and verify one in Targets → Test pool.
                </div>
              ) : rows.map((t) => (
                <button
                  key={t.target_id}
                  disabled={callMut.isPending}
                  onClick={() => callMut.mutate(t.target_id)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: "var(--radius-input)",
                    border: "1px solid var(--border-subtle)",
                    background: "var(--bg-paper)",
                    color: "var(--text-primary)",
                    cursor: callMut.isPending ? "wait" : "pointer",
                    textAlign: "left",
                    width: "100%",
                    opacity: callMut.isPending ? 0.7 : 1,
                  }}
                >
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 500 }}>{t.full_name}</span>
                    <span
                      title="OTP-verified test number"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 4,
                        padding: "2px 8px",
                        borderRadius: 999,
                        fontSize: 9.5,
                        fontWeight: 600,
                        textTransform: "uppercase",
                        letterSpacing: "0.05em",
                        color: "var(--success)",
                        background: "color-mix(in srgb, var(--success) 12%, transparent)",
                        border: "1px solid color-mix(in srgb, var(--success) 35%, transparent)",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <CheckCircle2 size={10} strokeWidth={2} />
                      Verified
                    </span>
                  </span>
                  <span style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)", whiteSpace: "nowrap" }}>{t.primary_phone_e164}</span>
                </button>
              ))}
            </div>
            <button onClick={onClose} style={{
              padding: "6px 14px",
              borderRadius: "var(--radius-input)",
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              cursor: "pointer",
            }}>Cancel</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Recent calls sub-component ─────────────────────────────────────────────────

function RecentCallsForEngagement({ engagementId }: { engagementId: string }) {
  const { data, isLoading, error } = useEngagementCalls(engagementId);

  return (
    <Card title="Recent calls" accentColor="var(--accent)">
      {isLoading ? (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--text-muted)",
            padding: "8px 0",
          }}
        >
          Loading…
        </div>
      ) : error ? (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--error)",
            padding: "8px 0",
          }}
        >
          Failed to load
        </div>
      ) : !data || data.items.length === 0 ? (
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            color: "var(--text-muted)",
            padding: "8px 0",
          }}
        >
          No calls yet
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {data.items.slice(0, 10).map((call) => (
            <Link
              key={call.call_id}
              to={`/live-calls/${call.call_id}`}
              data-testid="engagement-call-row"
              style={{
                display: "grid",
                gridTemplateColumns: "16px 1fr auto 12px",
                gap: 8,
                alignItems: "center",
                fontSize: 11,
                color: "var(--text-secondary)",
                textDecoration: "none",
                padding: "7px 0",
                fontFamily: "var(--font-mono)",
                borderBottom: "1px dashed var(--border-subtle)",
              }}
            >
              <span style={{ color: "var(--accent)", textAlign: "center" }}>
                {call.has_recording ? (
                  <span title="Recording attempted">▶</span>
                ) : (
                  <span style={{ color: "var(--text-muted)" }}>·</span>
                )}
              </span>
              <span style={{ letterSpacing: "0.04em" }}>
                {call.call_id.slice(0, 8)}{" "}
                <span style={{ color: "var(--text-muted)" }}>·</span>{" "}
                <span style={{ color: callStateColor(call.state) }}>{call.state.toLowerCase()}</span>
                {call.verdict ? (
                  <>
                    {" "}
                    <span style={{ color: "var(--text-muted)" }}>·</span>{" "}
                    <span style={{ color: callVerdictColor(call.verdict) }}>{call.verdict.toLowerCase()}</span>
                  </>
                ) : null}
              </span>
              <span
                style={{
                  color: "var(--text-muted)",
                  fontVariantNumeric: "tabular-nums",
                }}
                title={`${call.turn_count} turn${call.turn_count === 1 ? "" : "s"} · ${call.rejection_count} rejection${call.rejection_count === 1 ? "" : "s"}`}
              >
                {call.turn_count}t
                {call.rejection_count > 0 ? `·${call.rejection_count}r` : ""}
              </span>
              <ChevronRight size={11} strokeWidth={1.5} color="var(--text-muted)" />
            </Link>
          ))}
          {data.total > 10 ? (
            <div
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: 9.5,
                color: "var(--text-muted)",
                marginTop: 6,
                letterSpacing: "0.12em",
                textTransform: "uppercase",
              }}
            >
              + {data.total - 10} more
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function StatusBlock({
  label,
  tone,
  children,
}: {
  label: string;
  tone?: "error";
  children?: React.ReactNode;
}) {
  return (
    <div
      style={{
        padding: "32px 24px",
        textAlign: "center",
        background: "var(--bg-card)",
        border: "1px solid var(--border-subtle)",
        borderRadius: "var(--radius-card)",
        color: tone === "error" ? "var(--error)" : "var(--text-muted)",
        fontFamily: "var(--font-mono)",
        fontSize: 11,
        letterSpacing: "0.14em",
        textTransform: "uppercase",
      }}
    >
      {label}
      {children}
    </div>
  );
}

function Dot() {
  return (
    <span
      aria-hidden
      style={{
        display: "inline-block",
        width: 3,
        height: 3,
        background: "var(--text-muted)",
        margin: "0 10px 2px",
        verticalAlign: "middle",
      }}
    />
  );
}

// ─── Agent-tools toggle ─────────────────────────────────────────────────────────

const AGENT_TOOL_LABELS: Record<AgentToolName, { label: string; hint: string }> = {
  record_credential: {
    label: "record_credential",
    hint: "Capture credentials the target discloses (username, password, MFA code, security question).",
  },
  record_pii: {
    label: "record_pii",
    hint: "Capture PII the target discloses (DoB, SSN last-4, address, badge number, etc.).",
  },
  flag_corp_data: {
    label: "flag_corp_data",
    hint: "Flag corporate intel useful for follow-on attack (system names, software versions, org-chart relationships).",
  },
  end_call: {
    label: "end_call",
    hint: "Let the agent end the call cleanly when it hits voicemail, a wrong number, or the pretext is done.",
  },
};

function AgentToolsToggle({
  enabled,
  onChange,
  busy,
}: {
  enabled: string[];
  onChange: (next: AgentToolName[]) => void;
  busy: boolean;
}) {
  const isOn = (name: AgentToolName) => enabled.includes(name);
  const toggle = (name: AgentToolName) => {
    const next = isOn(name)
      ? (enabled.filter((n) => n !== name) as AgentToolName[])
      : ([...(enabled as AgentToolName[]), name] as AgentToolName[]);
    onChange(next);
  };
  const allOff = enabled.length === 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <p
        style={{
          margin: 0,
          fontSize: 11.5,
          color: "var(--text-muted)",
          lineHeight: 1.5,
        }}
      >
        Tools the agent may invoke mid-call to capture structured evidence.
        Every invocation lands in the audit chain. Defaults to off — turn on
        only the captures this engagement&apos;s RoE permits.
      </p>
      {allOff ? (
        <div
          style={{
            fontSize: 11,
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
            padding: "6px 0",
          }}
        >
          All tools disabled
        </div>
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {AGENT_TOOL_NAMES.map((name) => {
          const meta = AGENT_TOOL_LABELS[name];
          const on = isOn(name);
          return (
            <label
              key={name}
              style={{
                display: "grid",
                gridTemplateColumns: "auto 1fr",
                gap: 12,
                alignItems: "start",
                padding: "10px 12px",
                background: on ? "var(--accent-subtle)" : "var(--bg-tertiary)",
                border: `1px solid ${on ? "var(--accent-line)" : "var(--border-subtle)"}`,
                borderRadius: "var(--radius-input)",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.7 : 1,
                transition: "background 120ms, border-color 120ms",
              }}
            >
              <input
                type="checkbox"
                checked={on}
                disabled={busy}
                onChange={() => toggle(name)}
                style={{ marginTop: 3 }}
              />
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    color: on ? "var(--accent)" : "var(--text-primary)",
                    fontWeight: 500,
                  }}
                >
                  {meta.label}
                </span>
                <span style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.45 }}>
                  {meta.hint}
                </span>
              </div>
            </label>
          );
        })}
      </div>
    </div>
  );
}

// ─── Call-recording toggles ─────────────────────────────────────────────────────

function RecordingToggleRow({
  label,
  hint,
  on,
  busy,
  onToggle,
}: {
  label: string;
  hint: string;
  on: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  return (
    <label
      style={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        gap: 12,
        alignItems: "start",
        padding: "10px 12px",
        background: on ? "var(--accent-subtle)" : "var(--bg-tertiary)",
        border: `1px solid ${on ? "var(--accent-line)" : "var(--border-subtle)"}`,
        borderRadius: "var(--radius-input)",
        cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.7 : 1,
        transition: "background 120ms, border-color 120ms",
      }}
    >
      <input
        type="checkbox"
        checked={on}
        disabled={busy}
        onChange={onToggle}
        style={{ marginTop: 3 }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            color: on ? "var(--accent)" : "var(--text-primary)",
            fontWeight: 500,
          }}
        >
          {label}
        </span>
        <span style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.45 }}>
          {hint}
        </span>
      </div>
    </label>
  );
}

function RecordingToggles({
  recordingEnabled,
  disclosureRequired,
  onChange,
  busy,
}: {
  recordingEnabled: boolean;
  disclosureRequired: boolean;
  onChange: (next: {
    recording_enabled?: boolean;
    recording_disclosure_required?: boolean;
  }) => void;
  busy: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <p
        style={{
          margin: 0,
          fontSize: 11.5,
          color: "var(--text-muted)",
          lineHeight: 1.5,
        }}
      >
        Per-engagement call recording. When on, the worker writes an MP3 of each
        call to S3 for later playback. Changes save automatically and apply to
        calls dispatched from now on.
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <RecordingToggleRow
          label="Record calls"
          hint="Capture an MP3 recording of every call placed by this engagement."
          on={recordingEnabled}
          busy={busy}
          onToggle={() => onChange({ recording_enabled: !recordingEnabled })}
        />
        <RecordingToggleRow
          label="Play recording disclosure"
          hint="Speak the recording-disclosure announcement at the start of each call. Turning it off means no per-call recording announcement — consent is covered by the engagement authorization (ROE)."
          on={disclosureRequired}
          busy={busy}
          onToggle={() =>
            onChange({ recording_disclosure_required: !disclosureRequired })
          }
        />
      </div>
    </div>
  );
}

// ─── Streaming-gating toggle ─────────────────────────────────────────────────────

function StreamingGatingToggle({
  enabled,
  onChange,
  busy,
}: {
  enabled: boolean;
  onChange: (next: { streaming_gating_enabled?: boolean }) => void;
  busy: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <RecordingToggleRow
        label="Streaming gating (faster replies)"
        hint="Emit speech as the model produces it, validating each chunk in parallel — replies start ~700ms sooner. Off = the full reply is safety-checked before any audio plays (default)."
        on={enabled}
        busy={busy}
        onToggle={() => onChange({ streaming_gating_enabled: !enabled })}
      />
      <p
        style={{
          margin: 0,
          fontSize: 11.5,
          color: "var(--warning)",
          lineHeight: 1.5,
        }}
      >
        &#x26A0; Trades the fail-closed guarantee for faster replies. On a mid-stream
        policy violation, up to ~500&#8211;700&nbsp;ms of unsafe audio may reach the
        target before the bot is cut off (every chunk is still audited).
        Off = full fail-closed review (default).
      </p>
    </div>
  );
}

// ─── Max-turns cap ────────────────────────────────────────────────────────────────

function MaxTurnsControl({
  value,
  onChange,
  busy,
}: {
  value: number | null;
  onChange: (next: number | null) => void;
  busy: boolean;
}) {
  const [draft, setDraft] = useState<string>(value !== null ? String(value) : "");
  const dirty = draft !== (value !== null ? String(value) : "");

  const commit = () => {
    if (draft === "") {
      onChange(null);
      return;
    }
    const n = parseInt(draft, 10);
    if (Number.isFinite(n) && n >= 1) {
      onChange(n);
    } else {
      setDraft(value !== null ? String(value) : "");
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <p
        style={{ margin: 0, fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.5 }}
      >
        Maximum user+bot exchange pairs before the agent gracefully wraps up the
        call and disconnects. Leave blank for no cap. Setting a low value (e.g. 4
        for fast credential-elicit, 12 for longer pretexts) bounds both budget
        and detection risk.
      </p>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <input
          type="number"
          min={1}
          step={1}
          placeholder="No cap"
          value={draft}
          disabled={busy}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          }}
          style={{
            ...inputStyle,
            maxWidth: 140,
            fontFamily: "var(--font-mono)",
            opacity: busy ? 0.7 : 1,
          }}
        />
        <span style={{ fontSize: 11.5, color: "var(--text-muted)" }}>turns</span>
        {dirty ? (
          <span
            style={{
              fontSize: 11,
              color: "var(--accent)",
              fontFamily: "var(--font-mono)",
              letterSpacing: "0.10em",
              textTransform: "uppercase",
            }}
          >
            Tab or Enter to save
          </span>
        ) : null}
      </div>
    </div>
  );
}
